import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import type { WhatsAppTemplateVariableMapping } from '../../models/MasterAdmin/whatsappHub';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import { parseConsecutiveTemplateSlots, slotValidationMessage } from './whatsappTemplateSlots';
import { findUnpairedWhatsAppFormatMarkers } from './whatsappTextFormatting';
import { isMediaHeaderFormat } from './whatsappPreview';
import { validateTemplateMappings } from './WhatsAppTemplateMappingsSection';
import {
  buttonsFromTemplate,
  buttonsToApiPayload,
  TEMPLATE_FOOTER_MAX,
  validateTemplateButtons,
  type TemplateButtonDraft,
} from './whatsappTemplateButtons';

export type TemplateHeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type TemplateCategory = 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
export type TemplateType = 'SIMPLE' | 'WITH_BUTTONS';

export type TemplateFormState = {
  name: string;
  displayName: string;
  category: TemplateCategory;
  languageCode: string;
  templateType: TemplateType;
  bodyPreview: string;
  variableCount: number;
  headerFormat: TemplateHeaderFormat;
  headerText: string;
  headerSampleUrl: string | null;
  headerSampleFileName: string | null;
  defaultVariableMappings: WhatsAppTemplateVariableMapping[];
  footer: string;
  includeOptOut: boolean;
  buttons: TemplateButtonDraft[];
};

export const META_TEMPLATE_BODY_MAX_LENGTH = 1024;

export const emptyForm = (): TemplateFormState => ({
  name: '',
  displayName: '',
  category: 'MARKETING',
  languageCode: 'en',
  templateType: 'WITH_BUTTONS',
  bodyPreview: '',
  variableCount: 0,
  headerFormat: 'NONE',
  headerText: '',
  headerSampleUrl: null,
  headerSampleFileName: null,
  defaultVariableMappings: [],
  footer: '',
  includeOptOut: false,
  buttons: [],
});

export function normalizeInteraktTemplateName(raw: string): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function isValidInteraktTemplateName(name: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(name);
}

export function validateInteraktTemplateText(text: string, fieldLabel = 'Body'): string | null {
  const value = String(text || '').trim();
  if (!value || !/\{\{\d+\}\}/.test(value)) return null;
  if (/^\{\{\d+\}\}/.test(value)) {
    return `${fieldLabel}: add static text before the first {{n}} placeholder. Meta does not allow variables at the start.`;
  }
  if (/\{\{\d+\}\}$/.test(value)) {
    return `${fieldLabel}: add static text after the last {{n}} placeholder. Meta does not allow variables at the end.`;
  }
  return null;
}

export function validateInteraktTemplateContent(body: string, headerText?: string): string | null {
  return (
    validateInteraktTemplateText(body, 'Body') ||
    (headerText ? validateInteraktTemplateText(headerText, 'Header') : null)
  );
}

export function validateTemplateBodyLength(body: string, fieldLabel = 'Body'): string | null {
  const length = String(body || '').length;
  if (length <= META_TEMPLATE_BODY_MAX_LENGTH) return null;
  return (
    `${fieldLabel} is too long (${length} characters). Meta allows max ${META_TEMPLATE_BODY_MAX_LENGTH}. ` +
    `Shorten by ${length - META_TEMPLATE_BODY_MAX_LENGTH} character(s).`
  );
}

export function optOutFooter(form: TemplateFormState): string {
  if (form.includeOptOut) return form.footer.trim() || 'Reply STOP to opt out';
  return form.footer.trim();
}

export function resolvedTemplateButtons(
  form: TemplateFormState,
  campaignsById?: Map<string, DeepLinkCampaign>
) {
  if (form.templateType === 'SIMPLE') {
    return form.includeOptOut
      ? buttonsToApiPayload([], { includeOptOut: true, campaignsById })
      : [];
  }
  return buttonsToApiPayload(form.buttons, {
    includeOptOut: form.includeOptOut,
    campaignsById,
  });
}

export function templateToForm(t: WhatsAppTemplate): TemplateFormState {
  const headerFormat = (String(t.headerFormat || 'NONE').toUpperCase() as TemplateHeaderFormat) || 'NONE';
  const buttons = buttonsFromTemplate(t);
  const hasStopButton = (t.buttons || []).some(
    (b) => String(b.text || '').toLowerCase() === 'stop'
  );
  const footerHasStop = /stop/i.test(String(t.footer || ''));
  return {
    name: t.name,
    displayName: t.displayName || t.name,
    category: (String(t.category || 'UTILITY').toUpperCase() as TemplateCategory) || 'UTILITY',
    languageCode: t.languageCode || 'en',
    templateType: buttons.length || hasStopButton ? 'WITH_BUTTONS' : 'SIMPLE',
    bodyPreview: t.bodyPreview || '',
    variableCount: t.variableCount ?? 0,
    headerFormat: ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat)
      ? headerFormat
      : 'NONE',
    headerText: t.headerText || '',
    headerSampleUrl: t.headerHandleFileUrl || null,
    headerSampleFileName: t.headerHandleFileName || null,
    defaultVariableMappings: t.defaultVariableMappings || [],
    footer: t.footer || '',
    includeOptOut: hasStopButton || footerHasStop,
    buttons,
  };
}

export function validateTemplateForm(
  form: TemplateFormState,
  opts?: { isEdit?: boolean; campaignsById?: Map<string, DeepLinkCampaign> }
) {
  void opts?.isEdit;
  if (!form.bodyPreview.trim()) return 'Template body is required';
  const lengthError = validateTemplateBodyLength(form.bodyPreview.trim(), 'Body');
  if (lengthError) return lengthError;
  const placementError = validateInteraktTemplateContent(
    form.bodyPreview.trim(),
    form.headerFormat === 'TEXT' ? form.headerText.trim() : undefined
  );
  if (placementError) return placementError;
  const slotError = slotValidationMessage(form.bodyPreview.trim());
  if (slotError) return slotError;
  const formatError = findUnpairedWhatsAppFormatMarkers(form.bodyPreview.trim());
  if (formatError) return formatError;
  const mappingError = validateTemplateMappings(form.defaultVariableMappings, form.bodyPreview.trim());
  if (mappingError) return mappingError;
  const codeName = normalizeInteraktTemplateName(form.name);
  if (!codeName || !isValidInteraktTemplateName(codeName)) {
    return 'Template name must use lowercase letters, numbers, and underscores (e.g. course_promo_v1)';
  }
  if (form.headerFormat === 'TEXT' && !form.headerText.trim()) {
    return 'Header text is required for text-header templates';
  }
  if (isMediaHeaderFormat(form.headerFormat) && !form.headerSampleUrl) {
    return `Upload a sample ${form.headerFormat.toLowerCase()} for Meta review`;
  }
  if (form.footer.length > TEMPLATE_FOOTER_MAX) {
    return `Footer exceeds ${TEMPLATE_FOOTER_MAX} characters`;
  }
  if (form.templateType === 'WITH_BUTTONS') {
    const buttonError = validateTemplateButtons(form.buttons, opts?.campaignsById);
    if (buttonError) return buttonError;
  }
  return null;
}

export function normalizeBodyPreview(raw: string): string {
  return String(raw || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

export function buildTemplatePayload(
  form: TemplateFormState,
  campaignsById?: Map<string, DeepLinkCampaign>
) {
  const codeName = normalizeInteraktTemplateName(form.name);
  const bodyPreview = normalizeBodyPreview(form.bodyPreview);
  return {
    name: codeName,
    displayName: form.displayName.trim() || codeName,
    category: form.category,
    languageCode: form.languageCode,
    bodyPreview,
    variableCount: Number(form.variableCount) || parseConsecutiveTemplateSlots(bodyPreview).count,
    defaultVariableMappings: form.defaultVariableMappings,
    headerFormat: form.headerFormat,
    headerText: form.headerFormat === 'TEXT' ? form.headerText.trim() : undefined,
    headerHandleFileUrl: form.headerSampleUrl || undefined,
    headerHandleFileName: form.headerSampleFileName || undefined,
    footer: optOutFooter(form) || undefined,
    buttons: resolvedTemplateButtons(form, campaignsById),
  };
}

export function templateTypeLabel(type: TemplateType) {
  return type === 'WITH_BUTTONS' ? 'Template with Buttons' : 'Simple template';
}

export function categoryLabel(category: TemplateCategory) {
  return category.charAt(0) + category.slice(1).toLowerCase();
}
